// Shared helpers for resolving and applying branding returned by the login /
// validate-token APIs.
//
// Resolution order (first non-null wins):
//   1. branding.partnerBranding  -> logged-in user is a partner
//   2. branding.tenantBranding   -> logged-in user belongs to a tenant
//   3. DEFAULT_PREFERENCES       -> nothing configured yet

export const BRANDING_SOURCE = {
  PARTNER: "PARTNER",
  TENANT: "TENANT",
  DEFAULT: "DEFAULT",
};

export const DEFAULT_PREFERENCES = {
  notifications: true,
  notificationPreference: "email",
  defaultFacility: null,
  language: "en",
  themeGradientStart: "#1c2468",
  themeGradientEnd: "#0b1026",
  importBtnColor: "#4caf50",
  yesBtnColor: "#2196f3",
  noBtnColor: "#f44336",
};

// Fields that live on the branding document but are not user preferences.
const NON_PREFERENCE_KEYS = [
  "_id",
  "ownerType",
  "ownerId",
  "brandImage",
  "brandObjectName",
  "originalFileName",
  "mimeType",
  "fileSize",
  "createdBy",
  "updatedBy",
  "createdAt",
  "updatedAt",
  "logoUrlExpires",
  "expires",
];

export const resolveImageUrl = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image?.downloadUrl || image?.url || "";
};

// Strip non-preference fields and drop null/undefined so defaults still apply.
export const pickPreferences = (source) => {
  if (!source || typeof source !== "object") return {};

  // GET /brand returns preferences flat; login returns them under `preferences`.
  const raw =
    source.preferences && typeof source.preferences === "object"
      ? source.preferences
      : source;

  return Object.entries(raw).reduce((acc, [key, value]) => {
    if (NON_PREFERENCE_KEYS.includes(key)) return acc;
    if (value === null || value === undefined) return acc;
    acc[key] = value;
    return acc;
  }, {});
};

const unwrapUser = (userDetail) => userDetail?.data || userDetail || null;

/**
 * Resolve which branding applies to the logged-in user.
 *
 * @returns {{
 *   source: "PARTNER" | "TENANT" | "DEFAULT",
 *   branding: object | null,
 *   preferences: object,
 *   brandImage: string
 * }}
 */
export const resolveActiveBranding = (userDetail) => {
  const user = unwrapUser(userDetail);
  const branding = user?.branding || null;

  const partnerBranding = branding?.partnerBranding || null;
  const tenantBranding = branding?.tenantBranding || null;

  if (partnerBranding) {
    return {
      source: BRANDING_SOURCE.PARTNER,
      branding: partnerBranding,
      preferences: { ...DEFAULT_PREFERENCES, ...pickPreferences(partnerBranding) },
      brandImage: resolveImageUrl(partnerBranding.brandImage),
    };
  }

  if (tenantBranding) {
    return {
      source: BRANDING_SOURCE.TENANT,
      branding: tenantBranding,
      preferences: { ...DEFAULT_PREFERENCES, ...pickPreferences(tenantBranding) },
      brandImage: resolveImageUrl(tenantBranding.brandImage),
    };
  }

  return {
    source: BRANDING_SOURCE.DEFAULT,
    branding: null,
    preferences: { ...DEFAULT_PREFERENCES },
    brandImage: "",
  };
};

export const buildSidebarGradient = (start, end) =>
  `radial-gradient(280px 220px at 0% 0%, rgba(237, 0, 140, 0.38), transparent 64%), ` +
  `radial-gradient(260px 240px at 120% 100%, rgba(30, 113, 187, 0.45), transparent 60%), ` +
  `linear-gradient(180deg, ${start} 0%, ${end} 100%)`;

export const buildButtonGradient = (start, end) =>
  `linear-gradient(135deg, ${start} 0%, ${end} 100%)`;

// Push theme preferences into the CSS custom properties used across the app.
export const applyThemePreferences = (values) => {
  const prefs = { ...DEFAULT_PREFERENCES, ...(values || {}) };
  const root = document.documentElement.style;

  root.setProperty(
    "--sidebar-gradient",
    buildSidebarGradient(prefs.themeGradientStart, prefs.themeGradientEnd)
  );
  root.setProperty(
    "--button-gradient",
    buildButtonGradient(prefs.themeGradientStart, prefs.themeGradientEnd)
  );
  root.setProperty("--import-gradient", prefs.importBtnColor);
  root.setProperty("--yes-gradient", prefs.yesBtnColor);
  root.setProperty("--no-gradient", prefs.noBtnColor);
};
